/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { type CSSProperties } from "react";
import { Link } from "../components/ui/Link";
import { ChevronRight, Mail, Printer, Stethoscope } from "lucide-react";
import { listProviders, type ProviderSummary } from "../api/registry";
import { useChrome } from "../contexts/ChromeContext";
import { Badge } from "../components/ui/Badge";
import { RegistryDirectory } from "../components/RegistryDirectory";
import { providerName } from "../lib/registry";

export function ReferralProviders() {
  useChrome({}, []);
  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Referring providers
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          The people who refer families to Manatee. Open one to correct contact details, including
          the fax number our referral updates go to.
        </p>
      </header>
      <RegistryDirectory
        countNoun="provider"
        placeholder="Search providers by name"
        load={listProviders}
        itemKey={(provider) => provider.id}
        renderCard={(provider, index) => (
          <ProviderCard key={provider.id} provider={provider} index={index} />
        )}
        empty={{
          icon: <Stethoscope size={20} />,
          title: "No providers match",
          description: "Try another name, email, or organization.",
        }}
      />
    </div>
  );
}

function ProviderCard({ provider, index }: { provider: ProviderSummary; index: number }) {
  return (
    <li
      className="group border border-border bg-bg-elevated shadow-card lift enter relative flex h-full flex-col gap-3 rounded-3xl p-5"
      style={{ "--enter-delay": `${Math.min(index, 8) * 55 + 70}ms` } as CSSProperties}
    >
      <div className="min-w-0">
        <h2 className="truncate font-display text-xl leading-tight text-text-primary">
          <Link
            to={`/referrals/providers/${provider.id}`}
            className="after:absolute after:inset-0 after:rounded-3xl"
          >
            {providerName(provider.firstName, provider.lastName)}
          </Link>
        </h2>
        <p className="mt-0.5 truncate text-xs text-text-tertiary">
          {provider.email ?? "No email on file"}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {provider.faxNumber ? (
          <Badge tone="neutral">
            <Printer size={10} className="mr-1 inline-block" />
            Fax on file
          </Badge>
        ) : (
          <Badge tone="warning">No fax</Badge>
        )}
        {provider.receiveUpdates && (
          <Badge tone="success">
            <Mail size={10} className="mr-1 inline-block" />
            Receives updates
          </Badge>
        )}
      </div>
      <div className="mt-auto flex items-center justify-between gap-3 border-t border-border/60 pt-3 text-xs">
        <span className="text-text-tertiary">
          {provider.referralActivity.lastReferralAt !== null
            ? `Last referral ${new Date(provider.referralActivity.lastReferralAt).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}`
            : "No referrals yet"}
        </span>
        <span className="inline-flex items-center gap-1 font-medium text-text-tertiary transition-colors group-hover:text-text-primary">
          Open
          <ChevronRight
            size={14}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </span>
      </div>
    </li>
  );
}
